import {
  useToast,
  ConfirmationDialog,
  Button,
  ButtonProps,
  IconButton,
  DeleteIcon,
  MenuItem,
  TextInput,
  Column,
  Text,
} from "@hightouch/htui";
import { captureException } from "src/lib/monitoring";
import { capitalize } from "lodash";
import { forwardRef, ReactNode, useState } from "react";
import { HiddenFormLabel } from "./form/hidden-form-label";

type Props = {
  label: string;
  onDelete: () => Promise<unknown>;
  onSuccess?: () => void;
  isDisabled?: ButtonProps["isDisabled"];
  size?: ButtonProps["size"];
  variant?: "button" | "icon" | "menu";
  additionalConfirmation?: ReactNode;
  typeValueToConfirm?: string;
};

/**
 * Note: when using this component as a menu item, you must set `<Menu isLazy={false}>` on the parent menu,
 * otherwise the confirmation dialog will unmount when the menu is closed.
 */
export const DeleteButton = forwardRef<HTMLButtonElement, Props>(
  (
    {
      label,
      onDelete,
      size = "lg",
      variant = "button",
      onSuccess,
      additionalConfirmation,
      typeValueToConfirm,
      ...props
    },
    ref,
  ) => {
    const { toast } = useToast();
    const [isDeleting, setIsDeleting] = useState(false);
    const [confirmValue, setConfirmValue] = useState("");

    const onClick = () => {
      setIsDeleting(true);
    };

    const isStringConfirmation = typeof additionalConfirmation === "string";
    const confirmationMessage = `Are you sure you want to delete this ${label}?${isStringConfirmation ? ` ${additionalConfirmation}` : ""}`;

    return (
      <>
        {variant === "icon" ? (
          <IconButton
            aria-label={`Delete ${label}`}
            variant="danger"
            icon={DeleteIcon}
            onClick={onClick}
            {...props}
            ref={ref}
          />
        ) : variant === "menu" ? (
          <MenuItem
            icon={DeleteIcon}
            variant="danger"
            onClick={onClick}
            {...props}
            ref={ref}
          >
            Delete
          </MenuItem>
        ) : (
          <Button
            size={size}
            variant="warning"
            onClick={onClick}
            {...props}
            ref={ref}
          >
            Delete
          </Button>
        )}
        <ConfirmationDialog
          isOpen={isDeleting}
          onClose={() => {
            setIsDeleting(false);
            setConfirmValue("");
          }}
          onConfirm={async () => {
            try {
              await onDelete();
              toast({
                id: "delete-resource",
                title: `${capitalize(label)} deleted`,
                variant: "success",
              });
              onSuccess?.();
            } catch (e) {
              captureException(e);
              toast({
                id: "delete-resource",
                title: `Failed to delete ${label}`,
                variant: "error",
              });
            }
          }}
          title={`Delete ${label}`}
          confirmButtonText="Delete"
          variant="danger"
          isConfirmButtonDisabled={
            Boolean(typeValueToConfirm) && typeValueToConfirm !== confirmValue
          }
        >
          <Column gap={2}>
            <Text>{confirmationMessage}</Text>
            {!isStringConfirmation && additionalConfirmation}
          </Column>
          {typeValueToConfirm && (
            <Column>
              <Text mt={4} mb={2}>
                Type <Text fontWeight="semibold">{typeValueToConfirm}</Text> to
                confirm:
              </Text>
              <HiddenFormLabel
                label={`Type ${typeValueToConfirm} to confirm deletion`}
              >
                <TextInput
                  mb={1}
                  placeholder={typeValueToConfirm}
                  value={confirmValue}
                  onChange={(e) => setConfirmValue(e.target.value)}
                />
              </HiddenFormLabel>
            </Column>
          )}
        </ConfirmationDialog>
      </>
    );
  },
);

DeleteButton.displayName = "DeleteButton";
